import React, { useCallback, useEffect, useRef, useState } from "react";
import { Text, useTheme, Link, Title, Card, Chip, Stack, Button } from "@bookingcom/bui-react";
import Frame from "../Frame";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { FilterKey, FilterMeta, getFiltersMeta, StoreKeys } from "../../../constants/filtersMeta";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters";
import { useFiltersReset } from "../../../hooks/useFiltersReset";
import { FilterSection } from "@flights/search-ui";
import useIsInViewport from "../../../hooks/useIsInViewport";
import flights_web_budget_layover_airports_filters from "../../../utils/experiments/funnel/flights_web_budget_layover_airports_filters";
import styles from "components/elements/SearchFilters/SearchFilters.module.css";
import useProfiler from "../../../hooks/useProfiler";

// ----------------------------------------------------------------------------
// FilterHeader
// ----------------------------------------------------------------------------
export const FilterHeader = ({
  title,
  action,
  onClick,
  showAction = true
}: {
  title: string;
  action: string;
  onClick?: () => void;
  showAction?: boolean;
}) => {
  return (
    <Frame direction="row" mb={3}>
      <Frame grow={1} alignItems="flex-start" justifyContent="flex-end">
        <Title title={title} variant="strong_2" titleTagName="h3" />
      </Frame>
      {showAction && (
        <Frame grow={1} alignItems="flex-end" justifyContent="flex-end">
          <Link text={action} onClick={onClick} attributes={{ "data-testid": "search_filter_airline_reset" }} />
        </Frame>
      )}
    </Frame>
  );
};

// ----------------------------------------------------------------------------
// AppliedFilter
// ----------------------------------------------------------------------------
export const AppliedFilter = ({ label, onClick }: { label: string; onClick: () => void }) => {
  const theme = useTheme();
  const [active, setActive] = useState(false);

  return (
    <Frame mb={3}>
      <Card fill={true}>
        <Frame direction="row" alignItems="center">
          <Frame grow={1} m={4}>
            <Text variant="strong_2">{label}</Text>
          </Frame>
          <Frame
            p={6}
            alignItems="flex-end"
            attributes={{
              style: {
                width: "auto",
                borderLeft: `${theme.units.border_width_100} solid ${theme.colors.color_border_alt}`,
                backgroundColor: active ? theme.colors.color_background_base_alt : "",
                transition: "all .2s"
              }
            }}
          >
            <button
              style={{ fontSize: 16, fontWeight: 700, color: theme.colors.color_foreground_alt }}
              onClick={onClick}
              onPointerDown={() => setActive(true)}
              onPointerUp={() => setActive(false)}
            >
              X
            </button>
          </Frame>
        </Frame>
      </Card>
    </Frame>
  );
};

// ----------------------------------------------------------------------------
// AppliedFilterList
// ----------------------------------------------------------------------------
interface AppliedFilterListProps {
  appliedFilters: any[];
  onDelete: (id: string) => void;
  onReset: () => void;
}

export const AppliedFilterList = ({ appliedFilters, onDelete, onReset }: AppliedFilterListProps) => {
  return (
    <Frame mt={4} mb={4}>
      <FilterHeader title="Applied filters" action="Reset all filters" onClick={onReset} />
      {appliedFilters.map((f, i) => {
        return <AppliedFilter key={i} label={f} onClick={() => onDelete(f)} />;
      })}
    </Frame>
  );
};

// ----------------------------------------------------------------------------
// AppliedFilterChip
// ----------------------------------------------------------------------------
export type FilterRowProps = {
  filterMeta: FilterMeta;
  resetFilter: (props: StoreKeys[]) => void;
  chipIndex: number;
  onChipDismissed: (index: number) => void;
};

export const FilterChip: React.FunctionComponent<FilterRowProps> = ({
  filterMeta,
  resetFilter,
  chipIndex,
  onChipDismissed
}) => {
  const i18n = useI18n() as I18nChildContext;
  const { copy, storeKeys } = filterMeta;

  if (!copy || !storeKeys) return null;

  const handleDismiss = () => {
    onChipDismissed(chipIndex);
    resetFilter(storeKeys);
    if (storeKeys.includes("duration")) resetFilter(["maxLayoverDuration"]);
  };

  return (
    <Chip
      dismissible
      label={copy}
      onChange={handleDismiss}
      attributes={{
        "aria-label": `${i18n.trans(t("a11y_flights_filters_chip_remove"))} ${copy}`,
        "data-search-reset-filter": `${storeKeys}`
      }}
    />
  );
};

// ----------------------------------------------------------------------------
// AppliedFilterChipsList
// ----------------------------------------------------------------------------
export const AppliedFilterChipsList = () => {
  const appliedFilters = useAppliedFilters();
  const i18n = useI18n() as I18nChildContext;
  const { isMobile } = useProfiler();
  const filtersMeta = getFiltersMeta(i18n);
  const { resetFilter, resetAllFilters } = useFiltersReset();

  const prevFiltersLengthRef = useRef<number>(appliedFilters.length);
  const lastDismissedIndexRef = useRef<number>(-1);
  const filtersContainerRef = useRef<HTMLDivElement>(null);

  const focusFirstElement = useCallback(() => {
    const parentContainer = document.querySelector("#search_filters_content_wrapper");

    if (parentContainer) {
      const focusableElements = parentContainer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, []);

  const handleChipDismissed = useCallback(
    (index: number) => {
      lastDismissedIndexRef.current = index;
      prevFiltersLengthRef.current = appliedFilters.length;

      setTimeout(() => {
        if (appliedFilters.length - 1 === 0) {
          focusFirstElement();
        } else {
          const chipButtons = document.querySelectorAll("[data-search-reset-filter]");
          const nextIndex = Math.min(index, chipButtons.length - 1);
          if (chipButtons.length > 0 && nextIndex >= 0) {
            (chipButtons[nextIndex] as HTMLElement).focus();
          }
        }
      }, 0);
    },
    [appliedFilters.length, focusFirstElement]
  );

  const handleResetAllFilters = useCallback(() => {
    resetAllFilters();
    setTimeout(focusFirstElement, 0);
  }, [resetAllFilters, focusFirstElement]);

  useEffect(() => {
    if (appliedFilters.length < prevFiltersLengthRef.current) {
      if (appliedFilters.length === 0) {
        setTimeout(focusFirstElement, 0);
      } else if (lastDismissedIndexRef.current !== -1) {
        setTimeout(() => {
          const chipButtons = document.querySelectorAll("[data-search-reset-filter]");
          const nextIndex = Math.min(lastDismissedIndexRef.current, chipButtons.length - 1);
          if (chipButtons.length > 0 && nextIndex >= 0) {
            (chipButtons[nextIndex] as HTMLElement).focus();
          }
        }, 0);
      }
    }
    prevFiltersLengthRef.current = appliedFilters.length;
  }, [appliedFilters.length, focusFirstElement]);

  return appliedFilters.length > 0 ? (
    <FilterSection
      attributes={{ "data-testid": "applied_filters_chips_list" }}
      title={{
        text: i18n.trans(t("flights_filters_selected_title")),
        tagName: "h3",
        attributes: { id: "applied_filters" },
        variant: "strong_2"
      }}
    >
      <div ref={filtersContainerRef}>
        <Stack direction="row" alignItems="center" gap={1}>
          {appliedFilters.map((filter: FilterKey, index) => (
            <FilterChip
              key={`${filter}-${index}`}
              filterMeta={filtersMeta[filter]}
              resetFilter={resetFilter}
              chipIndex={index}
              onChipDismissed={handleChipDismissed}
            />
          ))}
          <Button
            text={i18n.trans(t("flights_filters_selected_reset"))}
            variant="tertiary"
            type="button"
            size={isMobile() ? "large" : "medium"}
            attributes={{ "data-testid": "search_filters_selected_reset" }}
            onClick={handleResetAllFilters}
          />
        </Stack>
      </div>
    </FilterSection>
  ) : null;
};

// ----------------------------------------------------------------------------
// NewFiltersBaseViewportTracker
// ----------------------------------------------------------------------------
export const NewFiltersBaseViewportTracker = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(ref);
  useEffect(() => {
    if (isInViewport) {
      flights_web_budget_layover_airports_filters.stages.any_new_filter_in_viewport();
    }
  }, [isInViewport]);
  return <div ref={ref} className={styles.addNewFilters}></div>;
};
